import { useMemo } from 'react'
import type { RangeKey } from '@dataTypes/dashboard.types'
import {
  getProductionByProcedure,
  getProductionByProcedureOverview,
} from '@services/api/dashboard/finance.api'
import type { ProductionByProcedureRow } from '@services/api/dashboard/finance.api'
import {
  categoryColorsFromLabels,
  computeChartAxis,
} from '@utils/dashboardFinance.util'
import {
  useDashboardPeriodFetch,
  useDashboardPeriodParams,
  type DashboardPeriodFetcher,
} from './useDashboardPeriod'
import {
  computeProductionByProcedureCardAxis,
  normalizeProductionByProcedureRows,
} from '@utils/productionByProcedureCategory.util'
import { isDashboardDevMocksEnabled } from '@utils/dashboardDevMocks.util'
import { hasNonZeroDashboardValues } from '@utils/dashboardSectionEmpty.util'
import { buildFinanceProductionByProcedureDetailMock } from '@components/pages/Dashboard/Finance/financeMockData'

/** Max categories shown on the Finance dashboard card; detail page shows all. */
export const FINANCE_PRODUCTION_BY_PROCEDURE_CARD_CATEGORY_LIMIT = 4

export type UseFinanceProductionByProcedureOptions = {
  range: RangeKey
  date: string
  /** When set, chart includes only the top N categories by production. */
  maxChartCategories?: number
  enabled?: boolean
  queued?: boolean
}

type ChartRow = {
  label: string
  value: number
  total: number
}

export type UseFinanceProductionByProcedureResult = {
  loading: boolean
  error: string | null
  /** Response has arrived (`data != null`); independent of nonzero content. */
  hasPayload: boolean
  hasData: boolean
  rows: ProductionByProcedureRow[]
  chartConfig: ReturnType<typeof computeChartAxis> & { data: ChartRow[] }
  categoryColors: Record<string, string>
}

function useFinanceProductionByProcedureFromFetcher({
  range,
  date,
  maxChartCategories,
  enabled = true,
  queued = false,
  fetcher,
}: UseFinanceProductionByProcedureOptions & {
  fetcher: DashboardPeriodFetcher<
    Awaited<ReturnType<typeof getProductionByProcedure>>
  >
}): UseFinanceProductionByProcedureResult {
  const queryParams = useDashboardPeriodParams(range, date)
  const { data, loading, error } = useDashboardPeriodFetch(
    queryParams,
    fetcher,
    { enabled, queued },
  )

  const rows: ProductionByProcedureRow[] = useMemo(
    () => normalizeProductionByProcedureRows(data?.rows),
    [data],
  )

  const chartConfig = useMemo(() => {
    const chartRows =
      maxChartCategories != null ? rows.slice(0, maxChartCategories) : rows
    const chartData: ChartRow[] = chartRows.map((row) => {
      const value = row.production / 1000
      return { label: row.category, value, total: value }
    })
    const axis =
      maxChartCategories != null
        ? computeProductionByProcedureCardAxis(chartData.map((d) => d.value))
        : computeChartAxis(chartData.map((d) => d.value))
    return { data: chartData, ...axis }
  }, [rows, maxChartCategories])

  const categoryColors = useMemo(
    () => categoryColorsFromLabels(chartConfig.data.map((d) => d.label)),
    [chartConfig.data],
  )

  const mockPayload = useMemo(
    () =>
      buildFinanceProductionByProcedureDetailMock(range, maxChartCategories),
    [range, maxChartCategories],
  )

  const useMocks =
    isDashboardDevMocksEnabled() && !loading && (!!error || rows.length === 0)

  const effectiveRows = useMocks ? mockPayload.rows : rows

  return {
    loading: useMocks ? false : loading,
    error: useMocks ? null : error,
    hasPayload: data != null,
    hasData: effectiveRows.some((row) =>
      hasNonZeroDashboardValues(row.production, row.number_of_procedures),
    ),
    rows: effectiveRows,
    chartConfig: useMocks ? mockPayload.chartConfig : chartConfig,
    categoryColors: useMocks ? mockPayload.categoryColors : categoryColors,
  }
}

export function useFinanceProductionByProcedure(
  options: UseFinanceProductionByProcedureOptions,
): UseFinanceProductionByProcedureResult {
  return useFinanceProductionByProcedureFromFetcher({
    ...options,
    fetcher: getProductionByProcedure,
  })
}

export function useFinanceProductionByProcedureOverview(
  options: UseFinanceProductionByProcedureOptions,
): UseFinanceProductionByProcedureResult {
  return useFinanceProductionByProcedureFromFetcher({
    ...options,
    fetcher: getProductionByProcedureOverview,
  })
}
